import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getCard, getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type { SectionItemDM } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import {
  FIGMA_MOBILE_SCREEN_WIDTH,
  FIGMA_SECTIONS_GAP,
  FIGMA_SECTIONS_GAP_MOBILE,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.figma';
import { getHeroAndTextSubtype } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/section-gaps/hero-and-text-gaps';
import {
  addGapBeforeSectionWithSectionId,
  addGapAfterSectionWithSectionId,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/section-gaps/section-gaps';
import { addHeroAndText } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/heroAndText/addHeroAndText';
import { addMerchandiser } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/merchandiser/addMerchandiser';
import { addHeroLeft } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/heroLeft/addHeroLeft';
import { addHeroRight } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/heroRight/addHeroRight';
import { addWithNoHero } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/noHero/addWithNoHero';
import { addThreeUp } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/threeUp/addThreeUp';

import type { BrandConcessionContentSectionDTO } from '~gateways/ownable.dtos';

/** Section Background Markers are 0-height cards injected into layout to mark the
 * ending of a section, and help calculate the section height.
 * They should be removed in postProcess
 */
function addSectionBackgroundMarker(
  viewData: ViewData,
  section: BrandConcessionContentSectionDTO
): ViewData {
  const card = getCard<PlaneTypes.Section_Background_Marker>({
    data: {
      ...section,
      sectionId: section.id,
      key: `section-bg-marker-${section.id}`,
    },
    type: PlaneTypes.Section_Background_Marker,
    size: { width: viewData.layoutWidth!, height: 0 },
  });
  card.key = `section-bg-marker-${section.id}`;
  viewData.layoutTable?.[0]?.push(card);

  return viewData;
}

export async function addSections(viewData: ViewData): Promise<ViewData> {
  if (!viewData.modelData.sections?.length) {
    return viewData;
  }

  let updatedData = viewData;

  // trick for executing async/await in a loop sequentially
  await (async () => {
    for (const section of updatedData.modelData.sections ?? []) {
      // HeroAndText handles the background itself
      if (section.template !== 'HeroAndText') {
        updatedData = addSectionBg(updatedData, section);
      }

      updatedData = addGapBeforeSectionWithSectionId(updatedData, section);
      // eslint-disable-next-line no-await-in-loop,require-atomic-updates
      updatedData = await addSectionFunctions[section.template](
        updatedData,
        section
      );
      updatedData = addGapAfterSectionWithSectionId(updatedData, section);

      // HeroAndText handles the background itself
      if (section.template !== 'HeroAndText') {
        updatedData = addSectionBackgroundMarker(updatedData, section);
      } else {
        // HeroAndTextBackground
      }
      const subtype = getHeroAndTextSubtype(section);
      if (subtype !== 'HeroNoText') {
        updatedData = addSectionBackgroundMarker(updatedData, section);
      }
    }
  })();

  return updatedData;
}

export const addSectionBg = (viewData: ViewData, section: SectionItemDM) => {
  if (!viewData.layoutTable?.[0]) {
    return viewData;
  }

  // Note: PlaneTypes.Section_Background cards do not actually participate in the layout flow
  // of the section. They are just rendered in background of the section.
  const sectionBgCard = getCard({
    // @ts-expect-error
    data: { ...section, key: `section-bg-${section.id}` },
    type: PlaneTypes.Section_Background,
    size: { width: viewData.layoutWidth!, height: 0 },
  });

  sectionBgCard.key = `section-bg-${section.id}`;
  viewData.layoutTable[0].push(sectionBgCard);
  return viewData;
};

export function addGapBeforeSectionGeneral(viewData: ViewData): ViewData {
  const height =
    getMultiSectionFigmaLayout(viewData) === 'full'
      ? FIGMA_SECTIONS_GAP
      : FIGMA_SECTIONS_GAP_MOBILE;

  viewData.layoutTable?.forEach(pushGapToColumn(height, viewData));
  return viewData;
}

const addSectionFunctions: Record<
  SectionItemDM['template'],
  (viewData: ViewData, section: SectionItemDM) => Promise<ViewData>
> = {
  NotSet: nullSection,
  Header: nullSection,
  HeroLeft: addHeroLeft,
  HeroRight: addHeroRight,
  Merchandiser5: addMerchandiser,
  Merchandiser4: addMerchandiser,
  Merchandiser3: addMerchandiser,
  HeroAndText: addHeroAndText,
  ThreeUp: addThreeUp,
  ProductShelf: addWithNoHero,
};

async function nullSection(viewData: ViewData): Promise<ViewData> {
  return viewData;
}

function getMultiSectionFigmaLayoutFromWidth(width: number) {
  return width > FIGMA_MOBILE_SCREEN_WIDTH ? 'full' : 'mobile';
}

export function getMultiSectionFigmaLayout(viewData: ViewData) {
  return getMultiSectionFigmaLayoutFromWidth(viewData.cameraProps.size.width);
}

export function pushGapToColumn(
  height: number,
  viewData: ViewData,
  color = 'black'
) {
  return (column: GridCard<PlaneTypes>[]) =>
    column.push(getGap({ width: viewData.columnWidth!, height }, color));
}
